// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import { getSystemColor } from "@turnerBlueprint/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    jsInjection = "jsInjection",
    htmlInjection = "htmlInjection",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.jsInjection]: string;
        [fields.htmlInjection]: string;
    };
}
type Props = OwnProps & HasShellContext;

const testStyles = {
    css: css`
        #test h1 {
            margin: 0;
            padding: 0;
            font-size: initial;
            font-weight: initial;
        }
    `,
};

const TrustedContentWidget: React.FC<Props> = props => {
    React.useEffect(() => {
        const script = document.createElement("script");
        script.type = "application/javascript";
        script.text = props.fields.jsInjection;
        script.async = true;
        document.getElementById("trustedContentWrapper")!.appendChild(script);
    });

    return (
        <StyledWrapper {...testStyles}>
            <div id="trustedContentWrapper">
                {/* eslint-disable react/no-danger */}
                <div dangerouslySetInnerHTML={{ __html: props.fields.htmlInjection }} />
                {props.shellContext.isInShell && props.shellContext.isEditing && (
                    <StyledWrapper style={{ padding: "10px", backgroundColor: `${getSystemColor("primary")}` }}>
                        <Typography color="common.ui1" variant="h6" style={{ marginBottom: 0 }}>
                            {translate("Trusted Content Widget")}
                        </Typography>
                    </StyledWrapper>
                )}
            </div>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        {
            name: fields.jsInjection,
            displayName: "Javascript",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "Enter Javascript code",
            placeholder: "Enter Javascript code",
        },
        {
            name: fields.htmlInjection,
            displayName: "HTML",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "Enter valid HTML",
            placeholder: "Enter HTML",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: withIsInShell(TrustedContentWidget),
    definition,
};

export default widgetModule;
